import type { Move } from '../../../schemas/move';

/**
 * Mosse iniziali del Paladino (manuale V1, rr. 4852-4966).
 *
 * - **4 mosse automatiche** (`type: 'starting'`): *Io Sono la Legge*,
 *   *Corazzato*, *Faro di Devozione*, *Missione*.
 * - **2 mosse di scelta** (`pickCount: 1`): *Ordine Sacro*,
 *   *Ordine Sovrannaturale*. Manuale r. 4946: "*Inoltre, scegli la natura
 *   del tuo Ordine*".
 *
 * **Pattern critici**:
 *
 * - ***Faro di Devozione*** (D4): mossa-config con 7 Aspetti del mondo
 *   terreno in `MoveSchema.choices` (`choiceCount: 1`). Fondamento (6
 *   opzioni) + nome dell'entità (testo libero) restano in description
 *   (FI-18 strutturerà più avanti).
 * - ***Missione*** (D5): description-only multi-gruppo (3 obiettivi + 6
 *   benedizioni + 6 obblighi). Pattern multi-gruppo, FI-18.
 * - ***Ordine Sacro*** (D2): mossa con `choices` su 3 Tradizioni
 *   (Negromanzia, Valore, Natura). È il **trigger del casting** del
 *   Paladino nel sistema `getActiveSpellLists`: la mossa attiva la
 *   spell-list `paladino-native` con `tradizione` presa da
 *   `selectedChoices[0]`. La Tradizione Natura richiede archetipo Elfo
 *   (FI-2 risolto via `SpellAccess.requiresArchetype`).
 * - ***Ordine Sovrannaturale*** (D3 + FI-27 chiuso, PR Warlock 11/12): mossa
 *   con `MoveSchema.choices` su 3 Vincoli (Catena/Tomo/Lama). Quando il PG
 *   sceglie *Vincolo del Tomo*, attiva la spell-list `warlock-native`
 *   Warlock-side (gestito in `services/spellAccess.ts`). Le altre 2 scelte
 *   lasciano il PG non casteggiante (placeholder).
 *
 * **Prerequisito *Io Sono la Legge***: la mossa elenca 4 alternative
 * (Faro di Devozione / Divinità / Divinità della Natura / Contratto). Per
 * il Paladino L1 il prereq è soddisfatto automaticamente perché *Faro di
 * Devozione* è anch'essa iniziale auto. La forma `prerequisites.anyOf` con
 * forward-references cross-class (`chierico.divinita`, `druido.divinita-della-natura`,
 * `warlock.contratto`) supporta il caso multiclasse.
 */

const ioSonoLaLegge: Move = {
  id: 'paladino.io-sono-la-legge',
  name: 'Io Sono la Legge',
  type: 'starting',
  classId: 'paladino',
  trigger:
    "Quando dai un ordine (non autolesionista) a un PNG sfruttando l'autorità che ti è concessa dall'entità cui sei devoto",
  description:
    "Tira+**CAR**. Con 10+, il PNG esegue il tuo comando senza pensarci due volte. Con 7-9, il PNG esegue il tuo ordine ma il GM sceglie un'opzione:\n\n- facendolo fa in modo di allontanarsi da te e poter scappare;\n- interpreta le tue parole in modo a lui conveniente;\n- serberà un profondo rancore nei tuoi confronti.\n\nCon un fallimento, invece, il PNG fa ciò che vuole, ottenendo un successo inaspettato.",
  roll: {
    stat: 'carisma',
    on10plus: { text: 'Il PNG esegue il tuo comando senza pensarci due volte.' },
    on7to9: { text: 'Il PNG esegue il tuo ordine ma il GM sceglie 1 opzione dalla lista.' },
    on6minus: { text: 'Il PNG fa ciò che vuole, ottenendo un successo inaspettato.' },
  },
  tags: [],
  // Manuale r. 4856: "Richiede: *Faro di Devozione*, *Divinità*, *Divinità
  // della Natura* o *Contratto*". Per il Paladino L1 *Faro di Devozione*
  // è iniziale auto, quindi il prereq è sempre soddisfatto. La forma
  // anyOf con forward-references supporta il caso multiclasse.
  prerequisites: [
    {
      anyOf: [
        'paladino.faro-di-devozione',
        'chierico.divinita',
        'druido.divinita-della-natura',
        'warlock.contratto',
      ],
    },
  ],
  replaces: [],
};

const corazzato: Move = {
  id: 'paladino.corazzato',
  name: 'Corazzato',
  type: 'starting',
  classId: 'paladino',
  trigger: 'Sempre (effetto continuo sulle armature indossate)',
  description: "Ignori l'etichetta *ingombrante* sulle armature che indossi.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

const faroDiDevozione: Move = {
  id: 'paladino.faro-di-devozione',
  name: 'Faro di Devozione',
  type: 'starting',
  classId: 'paladino',
  trigger: "Alla creazione del personaggio (configurazione dell'Ordine)",
  description:
    "Il tuo Ordine è devoto a un'entità ultraterrena. Scegli a quali **Aspetti del mondo terreno** è interessata e il **Fondamento** della tua devozione verso l'entità sovrannaturale. Indica anche nome e natura dell'entità.\n\nSe prendi questa mossa con un avanzamento multiclasse, ottieni anche, a tua scelta, *Ordine Sacro* oppure *Ordine Sovrannaturale*. Ma ricorda, *non ottieni* questa mossa se hai già *Divinità* (Chierico), *Divinità della Natura* (Druido) o *Contratto* (Warlock).",
  tags: [],
  prerequisites: [],
  replaces: [],
  // FI-18 Fase 2: migrato da `choices` (Aspetti) + description-only Fondamento
  // a 2 `choiceGroups` strutturati. Aspetti = 7 opzioni, pickCount 1.
  // Fondamento = 6 opzioni, pickCount 1. Mantiene i 2 narrativeFields nome
  // + natura dell'entità ultraterrena.
  choiceGroups: [
    {
      id: 'aspetti',
      label: 'Aspetti del mondo terreno',
      description: "A quali aspetti è interessata l'entità ultraterrena che servi?",
      options: [
        { id: 'guarigione-ricostruzione', label: 'Guarigione e Ricostruzione' },
        { id: 'guerra-dominio', label: 'Guerra e Dominio' },
        { id: 'progresso-cultura', label: 'Progresso e Cultura' },
        { id: 'conoscenza-cose-nascoste', label: 'Conoscenza e Cose Nascoste' },
        { id: 'reietti-dimenticati', label: 'i Reietti e i Dimenticati' },
        { id: 'inganno-tradimento', label: 'Inganno e Tradimento' },
        { id: 'prosperita-trionfo', label: 'Prosperità e Trionfo' },
      ],
      pickCount: 1,
    },
    {
      id: 'fondamento',
      label: 'Fondamento della devozione',
      description: 'Come pratichi la devozione verso la tua entità sovrannaturale?',
      options: [
        {
          id: 'sacrificio-se',
          label: 'Sacrificare parte di sé o offrire il proprio dolore',
          description: 'Corpo, mente o spirito.',
        },
        {
          id: 'predicare',
          label: 'Predicare la parola secondo la sua volontà',
        },
        {
          id: 'voti-rinuncia',
          label: 'Imporsi e mantenere voti di rinuncia',
        },
        {
          id: 'sacrificio-altri',
          label: 'Sacrificare il corpo o l’anima di altri esseri viventi',
          description: 'O loro simulacri.',
        },
        {
          id: 'benevolenza-pieta',
          label: 'Farsi portatore della sua benevolenza o della sua pietà',
        },
        {
          id: 'collera-vendetta',
          label: 'Farsi portatore della sua collera o della sua vendetta',
        },
      ],
      pickCount: 1,
    },
  ],
  narrativeFields: [
    {
      id: 'nome-entita',
      label: "Nome dell'entità ultraterrena",
      placeholder: 'es. Lyra del Crepuscolo, Vthak il Pungente…',
      required: true,
      multiline: false,
    },
    {
      id: 'natura-entita',
      label: "Natura dell'entità",
      placeholder: 'es. divina, fatata, demoniaca, ancestrale…',
      required: true,
      multiline: false,
    },
  ],
  // **FI-82 (2026-05-24)**: SSOT shift. La scelta «Ordine Sacro /
  // Sovrannaturale» (manuale V1 r. 4946: «Inoltre, scegli la natura del
  // tuo Ordine»; manuale rr. 4892-4893: «Se prendi questa mossa con un
  // avanzamento multiclasse, ottieni anche, a tua scelta, *Ordine Sacro*
  // oppure *Ordine Sovrannaturale*») vive ora qui, NON più in
  // `paladinoClassDefinition.startingChoiceGroups`. Conseguenze:
  //  - L1 Paladino: Faro è auto in `startingMoves` → la grant attiva la
  //    UI di scelta Ordine (sezione "Mosse concesse" del builder L1).
  //  - Multiclass (Guerriero L2 → *Compagno d'Armi* → Faro): stessa grant
  //    si attiva → scelta Ordine appare nella sezione granted-of-target
  //    del `LevelUpStepMulticlass`.
  //  - Cascade nested: se l'utente sceglie Ordine Sovrannaturale, il
  //    suo `grantsMoveChoice from-choice-mapping` propaga al Vincolo
  //    Warlock (FI-80/81 service `nestedSetup` già pronto).
  //
  // **Pivot dal piano iniziale Q2=A1**: invece di `from-group` con
  // lookup cross-class via classe-della-mossa, uso il nuovo kind
  // `from-list` con mosse inline. Pro: niente dipendenza da
  // `paladinoClassDefinition.startingChoiceGroups`, niente lookup
  // cross-class, dato self-contained sulla mossa.
  grantsMoveChoice: {
    kind: 'from-list',
    moveOptions: ['paladino.ordine-sacro', 'paladino.ordine-sovrannaturale'],
  },
};

const missione: Move = {
  id: 'paladino.missione',
  name: 'Missione',
  type: 'starting',
  classId: 'paladino',
  trigger:
    "Quando giuri di portare a compimento un'impresa nel nome dell'entità ultraterrena cui sei devoto",
  description:
    "Dichiara il tuo scopo e il bersaglio dell'impresa.\n\nSe il tuo intento favorisce gli interessi in questo mondo dell'entità ultraterrena cui sei devoto (a discrezione del GM), scegli fino a due benedizioni.\n\nIl GM ti dirà anche uno o più altri obblighi cui dovrai ottemperare.\n\nPuoi dedicarti a una sola *Missione* per volta. Se non pratichi in modo regolare il tuo Fondamento o contravvieni agli obblighi che ti ha dato il GM, perdi tutte le benedizioni ottenute, ma l'impresa sarà considerata conclusa solo se raggiungi il tuo obiettivo o se fallisci miseramente. Se fallisci, subisci una penalità di -1 continuato a **CAR** o **SAG** (a scelta del GM). **Puoi riavere le benedizioni o rimuovere la penalità dovuta al fallimento solo se ti redimi agli occhi dell'entità cui sei devoto (a discrezione del GM).**",
  tags: [],
  // Manuale r. 4909: "Richiede: *Faro di Devozione*, *Divinità* o *Divinità
  // della Natura*". *Faro di Devozione* è auto per il Paladino L1.
  prerequisites: [
    {
      anyOf: ['paladino.faro-di-devozione', 'chierico.divinita', 'druido.divinita-della-natura'],
    },
  ],
  replaces: [],
  // FI-18: 3 choiceGroups (scopo + benedizioni + obblighi) + 1 narrativeField
  // (bersaglio dello scopo, es. il nome del flagello / persona / verità).
  // Le benedizioni hanno pickCount=2 (max), gli obblighi sono dati dal GM e
  // possono essere 1+; il giocatore (o il GM via UI) seleziona quelli decisi.
  choiceGroups: [
    {
      id: 'scopo',
      label: 'Scopo',
      description: 'Dichiara il tuo scopo.',
      options: [
        {
          id: 'eliminare',
          label: 'Eliminare un grande flagello',
          description: 'Compi un giuramento di porre fine a una minaccia precisa.',
        },
        {
          id: 'difendere',
          label: 'Difendere qualcuno o qualcosa',
          description: 'Proteggi un bersaglio dalle iniquità che lo perseguitano.',
        },
        {
          id: 'scoprire',
          label: 'Scoprire la verità',
          description: 'Disvela un mistero o una falsità.',
        },
      ],
      pickCount: 1,
    },
    {
      id: 'benedizioni',
      label: 'Benedizioni (fino a 2)',
      description:
        "Se l'intento favorisce gli interessi dell'entità (a discrezione del GM), scegli fino a 2 benedizioni.",
      options: [
        {
          id: 'orientamento-infallibile',
          label: "Infallibile senso dell'orientamento",
          description: 'Verso un bersaglio specifico.',
        },
        {
          id: 'invulnerabilita',
          label: 'Invulnerabilità a qualcosa',
          description: 'Es. fuoco, armi da taglio, charme, ecc.',
        },
        {
          id: 'segno-autorita',
          label: "Segno inequivocabile dell'autorità ultraterrena",
        },
        { id: 'sensi-acuti', label: 'Sensi acuti che vedono oltre le menzogne' },
        { id: 'voce-trascendente', label: 'Voce che trascende ogni linguaggio' },
        { id: 'liberta-corpo', label: 'Libertà da fame, sete e sonno' },
      ],
      pickCount: 2,
    },
    {
      id: 'obblighi',
      label: 'Obblighi (decisi dal GM, almeno 1)',
      description: 'Il GM ti dirà uno o più obblighi da osservare per mantenere le benedizioni.',
      options: [
        {
          id: 'onore',
          label: 'Onore',
          description: 'Non usare tattiche da codardo o trucchi sporchi.',
        },
        {
          id: 'onesta',
          label: 'Onestà',
          description: 'Non bluffare, mentire o ingannare anche a fin di bene.',
        },
        {
          id: 'segretezza',
          label: 'Segretezza',
          description: "Nascondi la presenza dell'entità in questo mondo.",
        },
        {
          id: 'offerta',
          label: 'Offerta',
          description: "Dona all'entità ricchezze e oggetti di potere.",
        },
        {
          id: 'rituali',
          label: 'Rituali',
          description: 'Osserva in modo impeccabile le liturgie indicate.',
        },
        {
          id: 'altro-fondamento',
          label: 'Un altro Fondamento di Faro di Devozione',
          description: 'Aggiungi un Fondamento ulteriore al tuo cammino.',
        },
      ],
      pickCount: 1,
    },
  ],
  narrativeFields: [
    {
      id: 'bersaglio-scopo',
      label: 'Bersaglio dello scopo',
      placeholder:
        "Es. il drago Vermerot (eliminare), il villaggio di Helmgar (difendere), l'identità del Traditore (scoprire)…",
      required: true,
      multiline: false,
    },
  ],
};

const ordineSacro: Move = {
  id: 'paladino.ordine-sacro',
  name: 'Ordine Sacro',
  type: 'starting',
  classId: 'paladino',
  trigger: 'Alla creazione del personaggio (configurazione della Tradizione Sacra)',
  // **NB tecnica** (per dev, NON in description): questa mossa è il
  // trigger del casting del Paladino nel sistema `getActiveSpellLists`.
  // Il PG che la sceglie ottiene la spell-list `paladino-native` con la
  // Tradizione scelta in `selectedChoices[0]`. La Tradizione Natura
  // richiede archetipo Elfo (vincolo strutturato via
  // `SpellAccess.requiresArchetype`).
  description:
    "Il tuo Ordine è devoto a una divinità. Scegli la tua Tradizione Sacra, l'insieme di incantesimi che manifesta il potere del tuo dio.\n\nOttieni le mosse del Chierico *Lanciare Incantesimi Divini* e *Mana*, ma non *Divinità*. Sebbene questa mossa *non si consideri multiclasse*, per calcolare il tuo livello da Chierico segui le normali regole sulle mosse multiclasse. *Non prendere* questa mossa se hai già *Ordine Sovrannaturale*.",
  tags: [],
  prerequisites: ['paladino.faro-di-devozione'],
  replaces: [],
  choices: [
    { id: 'negromanzia', label: "Negromanzia (manipolazione della vita e della morte, l'aldilà)" },
    { id: 'valore', label: 'Valore (combattimento, difesa, ispirazione, forza divina)' },
    // FI-32: la Tradizione Natura è disponibile solo per Paladino-Elfo
    // (FI-2). Prima vincolata via mappa hardcoded in `moveChoiceConstraints`,
    // ora strutturalmente sull'opzione.
    { id: 'natura', label: 'Natura', requiresArchetype: 'elfo' },
  ],
  choiceCount: 1,
  // **FI-38 Opz. C (2026-05-14)**: bundle cross-class verso Chierico
  // (manuale V1 r. 4960: «Ottieni le mosse del Chierico *Lanciare
  // Incantesimi Divini* e *Mana*, ma non *Divinità*»). Il Paladino-Sacro
  // casta riusando letteralmente le mosse del Chierico (roll +SAG
  // ereditato — decisione C1 2026-05-14). La spell-list `paladino-native`
  // è discriminata dalla presenza di questa mossa-padre nel PG, mentre
  // il trigger tecnico del casting è `chierico.lanciare-incantesimi-divini`.
  bundles: ['chierico.lanciare-incantesimi-divini', 'chierico.mana'],
};

const ordineSovrannaturale: Move = {
  id: 'paladino.ordine-sovrannaturale',
  name: 'Ordine Sovrannaturale',
  type: 'starting',
  classId: 'paladino',
  trigger: 'Alla creazione del personaggio (alternativa a *Ordine Sacro*)',
  // **NB tecnica** (per dev): trigger del casting Warlock-side del
  // Paladino-Sovrannaturale nel sistema `getActiveSpellLists` quando
  // `selectedChoices = ['vincolo-del-tomo']`. Le altre 2 scelte
  // (Catena/Lama) lasciano il PG non casteggiante. I 3 Vincoli sono
  // cross-class refs strutturati al catalogo Warlock.
  description:
    'Il tuo Ordine è devoto a una forza sovrannaturale non divina. Scegli quale delle seguenti mosse del Warlock ti viene concessa in cambio della tua devozione: *Vincolo della Catena*, *Vincolo della Lama* oppure *Vincolo del Tomo*. Se scegli il *Vincolo del Tomo*, ottieni anche le mosse *Mana* e *Lanciare Incantesimi del Patrono*. *Non puoi prendere* questa mossa se hai già *Ordine Sacro*.',
  tags: [],
  prerequisites: ['paladino.faro-di-devozione'],
  replaces: [],
  choices: [
    {
      id: 'vincolo-della-catena',
      label: 'Vincolo della Catena',
      description:
        'Crei legami obbliganti aiutando significativamente personaggi e PNG, da usare come leva per *parlamentare*.',
    },
    {
      id: 'vincolo-del-tomo',
      label: 'Vincolo del Tomo',
      description:
        'Ottieni un Tomo con tutte le suppliche del Warlock + 2 incantesimi di 1° livello a scelta. Concede anche *Mana* e *Lanciare Incantesimi del Patrono*.',
    },
    {
      id: 'vincolo-della-lama',
      label: 'Vincolo della Lama',
      description:
        "Ottieni un'arma ultraterrena con tipologia, portata e 2 miglioramenti a tua scelta. Dado di danno minimo d8 quando la usi.",
    },
  ],
  choiceCount: 1,
  // **FI-38 Opz. C (2026-05-14, decisione C2)**: pattern
  // `grantsMoveChoice from-choice-mapping` cross-class verso Warlock
  // (riusa pattern D6/FI-30). Il giocatore sceglie un Vincolo →
  // riceve la corrispondente mossa Warlock come `source: 'granted'`.
  // Il bundle di `warlock.vincolo-del-tomo` (FI-38 base) propaga
  // automaticamente `warlock.mana` + `warlock.lanciare-incantesimi-del-patrono`
  // se la choice è 'vincolo-del-tomo' → simmetria col Warlock vero.
  // Gli altri 2 Vincoli (Catena/Lama) restano non casteggianti.
  grantsMoveChoice: {
    kind: 'from-choice-mapping',
    sourceMoveId: 'paladino.ordine-sovrannaturale',
    mapping: {
      'vincolo-della-catena': 'warlock.vincolo-della-catena',
      'vincolo-del-tomo': 'warlock.vincolo-del-tomo',
      'vincolo-della-lama': 'warlock.vincolo-della-lama',
    },
  },
};

/**
 * Mosse iniziali AUTOMATICHE (4): prese tutte al livello 1 senza scelta.
 */
export const paladinoStartingAutoMoves: readonly Move[] = [
  ioSonoLaLegge,
  corazzato,
  faroDiDevozione,
  missione,
] as const;

/**
 * Mosse iniziali di SCELTA (2): il giocatore ne sceglie UNA al livello 1
 * (`pickCount: 1`). *Ordine Sacro* attiva il casting `paladino-native`;
 * *Ordine Sovrannaturale* è description-only stub (FI-27).
 */
export const paladinoStartingChoiceMoves: readonly Move[] = [
  ordineSacro,
  ordineSovrannaturale,
] as const;

/** Tutte le mosse iniziali (auto + scelta). */
export const paladinoInitialMoves: readonly Move[] = [
  ...paladinoStartingAutoMoves,
  ...paladinoStartingChoiceMoves,
];
